import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  getNotifications, 
  markNotificationAsRead, 
  markAllNotificationsAsRead,
  deleteNotification,
  getUnreadNotificationCount
} from '@/services/notifications'

// Query keys
export const notificationKeys = {
  all: ['notifications'] as const,
  list: (userId: string, params?: any) => [...notificationKeys.all, 'list', userId, params] as const,
  unreadCount: (userId: string) => [...notificationKeys.all, 'unread-count', userId] as const,
}

// Get notifications
export const useNotifications = (userId?: string, params?: any) => {
  return useQuery({
    queryKey: notificationKeys.list(userId || '', params),
    queryFn: () => getNotifications(userId!, params),
    enabled: !!userId,
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
  })
}

// Get unread notification count
export const useUnreadNotificationCount = (userId?: string) => {
  return useQuery({
    queryKey: notificationKeys.unreadCount(userId || ''),
    queryFn: () => getUnreadNotificationCount(userId!),
    enabled: !!userId,
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
    staleTime: 10 * 1000, // 10 seconds
  })
}

// Mark notification as read
export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (notificationId: string) => markNotificationAsRead(notificationId),
    onSuccess: () => {
      // Invalidate all notification queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

// Mark all notifications as read
export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) => markAllNotificationsAsRead(userId),
    onSuccess: () => {
      // Invalidate all notification queries
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}

// Delete notification
export const useDeleteNotification = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (notificationId: string) => deleteNotification(notificationId),
    onSuccess: () => {
      // Invalidate all notification queries
      queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    },
  })
}